import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { Component, computed, Inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { Operation } from 'fast-json-patch';
import { firstValueFrom, Observable, of, Subject, takeUntil } from 'rxjs';
import { EntityRelationChangeTransactionDetailsComponent } from '../../../../core/components/entity-relation-change-transaction-details/entity-relation-change-transaction-details.component';
import { SaveChangesDialogComponent, SaveChangesResult } from '../../../../core/components/save-changes-dialog/save-changes-dialog.component';
import { DATE_FORMATS } from '../../../../core/config/date-formats';
import { EditablePostClientStatus } from '../../../../core/enums/editable-post-client-status';
import { MachineOwnerType, MachineOwnerTypeLabels } from '../../../../core/enums/machine-owner-type';
import { MachineType, MachineTypeLabels } from '../../../../core/enums/machine-type';
import { GetProductionTechnicianAreaEnum, ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../../core/enums/production-technician-area';
import { ComponentCanDeactivate } from '../../../../core/guards/pending-changes.guard';
import { ContractorLookupDto } from '../../../../core/models/contractors/contractor-lookup-dto';
import { MachineDetailsDto } from '../../../../core/models/machines/machine-details-dto';
import { MachineDetailsTeamInfoDto } from '../../../../core/models/machines/machine-details-team-info-dto';
import { MachineDetailsBaseInfoForm, MachineDetailsBiometriaInfoForm, MachineDetailsEquipmentInfoForm, MachineDetailsForm, MachineDetailsProductionInfoForm } from '../../../../core/models/machines/machine-form-models';
import { MachineStatus, MachineStatusLabels } from '../../../../core/models/machines/machine-status';
import { Stanford2010Version, Stanford2010VersionLabels } from '../../../../core/models/machines/stanford2010-version';
import { ContractorApiService } from '../../../../core/services/api/contractor-api.service';
import { MachineApiService } from '../../../../core/services/api/machine-api.service';
import { AuthService } from '../../../../core/services/auth.service';
import { DefinitionService } from '../../../../core/services/definition/definition.service';
import { IconService } from '../../../../core/services/icon.service';
import { NavigationService } from '../../../../core/services/navigation.service';
import { MachineDetailsService } from '../../services/machine-details.service';
import { MachinePerformanceService } from '../../services/machine-performance.service';
import { MachineWorkHoursService } from '../../services/machine-work-hours.service';
import { CreateMachineDialogComponent, CreateMachineModel } from '../create-machine-dialog/create-machine-dialog.component';
import { MachineAddTeamDialogComponent } from '../machine-add-team-dialog/machine-add-team-dialog.component';
import { MachinePerformanceComponent } from '../machine-performance/machine-performance.component';
import { MachineWorkHoursComponent } from '../machine-work-hours/machine-work-hours.component';
import { UpdateMachineModel } from '../../../../core/models/machines/update-machine-model';
import { CopyToClipboardButtonComponent } from '../../../../core/components/copy-to-clipboard-button/copy-to-clipboard-button.component';
import { MachineOwnerService } from '../../services/machine-owner.service';
import { TeamOwnerType } from '../../../../core/models/teams/team-owner-type';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-machine-details',
  imports: [MatIconModule, CommonModule, MatFormFieldModule,
    MatInputModule, MatSelectModule, FormsModule, MatButtonModule,
    ReactiveFormsModule, MatCheckboxModule, MatOptionModule,
    MatProgressSpinnerModule, MachinePerformanceComponent, MachineWorkHoursComponent,
    MatIconModule, MatTooltipModule, MatButtonModule, MatDatepickerModule,
    CopyToClipboardButtonComponent,
    FontAwesomeModule, EntityRelationChangeTransactionDetailsComponent],
  templateUrl: './machine-details.component.html',
  styleUrl: './machine-details.component.scss',
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'sv-SE' }
  ]
})
export class MachineDetailsComponent implements ComponentCanDeactivate {
  public machine!: MachineDetailsDto;

  private destroy$ = new Subject<void>();
  private baselineUpdateModel?: UpdateMachineModel;
  public machineComputersSorted = computed(() => {
    return this.definitionService.machineComputers().sort((a, b) => a.name.localeCompare(b.name));
  });
  machineForm!: FormGroup<MachineDetailsForm>;
  isLoading: boolean | undefined;
  machineIsUpdated: boolean = false;

  allContractors: ContractorLookupDto[] = [];
  machineTypeLabels = MachineTypeLabels;
  machineTypeEnum = Object.values(MachineType).filter(x => typeof x === "number");
  machineType = MachineType;
  ownerTypeLabels = MachineOwnerTypeLabels;
  ownerTypeEnum = Object.values(MachineOwnerType).filter(x => typeof x === "number");
  machineStatusLabels = MachineStatusLabels;
  machineStatusEnum = Object.values(MachineStatus).filter(x => typeof x === "number");
  stanford2010VersionLabels: Partial<Record<Stanford2010Version, string>> = Object.fromEntries(
    Object.entries(Stanford2010VersionLabels).filter(([key]) => this.isStanford2010VersionActive(Number(key)))
  );
  stanford2010VersionEnum = Stanford2010Version;
  stanford2010VersionEnumIterable = Object.values(Stanford2010Version)
    .filter((x): x is Stanford2010Version => typeof x === "number")
    .filter(x => this.isStanford2010VersionActive(x));
  machineFormHasChanges: boolean = false;
  machineHasOtherChanges: boolean = false;
  machineWorkHoursValidates = false;
  machineIsNew: boolean = true;
  duplicatedFrom: string | undefined;
  isReadonly: boolean = true;
  isDemoMachine: boolean = false;
  showStoredOwnerName: boolean = true;
  ignoreUpdateFields = ['machineModelText'];

  get ownerTypeText(): string {
    const val = this.machineForm?.get('baseInfo.ownerType')?.value;
    return val != null ? (this.ownerTypeLabels[val] ?? '') : '';
  }

  get saveDisabled(): boolean {
    if (!this.machineForm) {
      return true;
    }

    const hasChanges = this.machine?.id === 0 || this.machineHasOtherChanges || this.machineFormHasChanges;

    if (!hasChanges) {
      return true;
    }

    if (!this.machineWorkHoursValidates) {
      return true;
    }

    return !this.machineForm.valid;
  }

  get activeTeams(): MachineDetailsTeamInfoDto[] {
    if (!this.machine?.baseInfo?.teams) return [];
    return this.machine.baseInfo.teams.filter(t => t.clientStatus !== EditablePostClientStatus.Deleted && t.active);
  }

  get hasTeamConnections(): boolean {
    return this.machine?.baseInfo?.teams.some(t => t.clientStatus !== EditablePostClientStatus.Deleted) ?? false;
  }

  get companyIdField() {
    return this.machineForm.get('biometriaInfo.biometriaCompanyId')!;
  }

  constructor(private machineApiService: MachineApiService,
    private contractorApiService: ContractorApiService,
    public definitionService: DefinitionService,
    private route: ActivatedRoute,
    public authService: AuthService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
    private navigationService: NavigationService,
    public iconService: IconService,
    private workHoursService: MachineWorkHoursService,
    private performanceService: MachinePerformanceService,
    private machineDetailsService: MachineDetailsService,
    private machineOwnerService: MachineOwnerService,
    @Inject(PLATFORM_ID) private platformId: Object) {
  }

  async ngOnInit(duplicatedMachineId: number): Promise<void> {
    this.initializeForm();
    await this.loadContractors();

    const machineId = this.route.snapshot.paramMap?.get('machineId');
    if (machineId == '0' && !duplicatedMachineId) {

      const dialogRef = this.dialog.open(CreateMachineDialogComponent, {
        closeOnNavigation: true,
        autoFocus: false,
        restoreFocus: false
      });
      const dialogResult: CreateMachineModel = await firstValueFrom(dialogRef.afterClosed());
      if (!dialogResult) {
        this.router.navigate(['/machines']);
        return;
      }
      this.createNewMachine(dialogResult);

      return
    }

    await this.loadMachine(duplicatedMachineId);
    this.populateFormFromMachine();
    this.isReadonly = !this.authService.isPolicyPT();
    this.isDemoMachine = this.machine.baseInfo.isDemoMachine ?? false;
    this.loadQueryParams();
  }

  private loadQueryParams() {
    this.route.queryParams.subscribe(params => {
      this.machineIsNew = params['type'] === 'new' ? true : false;
    });
  }

  async loadContractors() {
    this.allContractors = await firstValueFrom(this.contractorApiService.getContractorLookups());
    this.updateShowStoredOwnerName();
  }

  public updateShowStoredOwnerName(): void {
    if (this.machine?.baseInfo.machineOwnerId && this.allContractors?.length &&
      !this.allContractors.some(c => c.id === this.machine.baseInfo.machineOwnerId)) {
      this.showStoredOwnerName = true;
      return;
    }

    this.showStoredOwnerName = false;
  }

  private initializeForm() {
    this.machineForm = this.fb.group({
      baseInfo: this.fb.group<MachineDetailsBaseInfoForm>({
        machineId: new FormControl(''),
        machineManufacturerId: new FormControl(null),
        machineManufacturerModelId: new FormControl(null),
        machineType: new FormControl(null),
        registrationNumber: new FormControl(''),
        manufacturingNumber: new FormControl(''),
        machineSizeText: new FormControl(''),
        created: new FormControl({ value: '', disabled: true }),
        machineOwnerId: new FormControl(null, {
          validators: [this.machineDetailsService.conditionalContractorValidator()]
        }),
        machineModelText: new FormControl({ value: '', disabled: true }),
        machineModelYear: new FormControl(null),
        status: new FormControl(null),
        ownerType: new FormControl(null),
        otherEquipment: new FormControl(''),
        isSchoolForwarder: new FormControl(false),
        isGrotForwarder: new FormControl(false),
        isDemoMachine: new FormControl(false),
        teams: new FormControl(''),
        notes: new FormControl('')
      }),
      biometriaInfo: this.fb.group<MachineDetailsBiometriaInfoForm>({
        biometriaCompanyId: new FormControl('', {
          validators: [this.machineDetailsService.biometriaCompanyIdValidator()]
        }),
        biometriaGpxId: new FormControl('', {
          validators: [this.machineDetailsService.biometriaGpxIdValidator()],
          asyncValidators: [this.machineDetailsService.biometriaGpxIdAsyncValidator(() => this.machine?.id ?? 0)]
        }),
        biometriaPassword: new FormControl('')
      }),
      equipmentInfo: this.fb.group<MachineDetailsEquipmentInfoForm>({
        machineComputerId: new FormControl(null),
        stinaVersion: new FormControl(''),
        windowsVersion: new FormControl(null),
        masenVersion: new FormControl(''),
        stanford2010Version: new FormControl(null),
        hasHighPrecisionCraneTipCoordinate: new FormControl(false),
        hasRtk: new FormControl(false),
        fileSendingActive: new FormControl(false),
        sendPinFilesForPulp: new FormControl(false),
        sendAptFilesViaMasen: new FormControl(false),
        useProductionFilesFromMasen: new FormControl(false)
      }),
      productionInfo: this.fb.group<MachineDetailsProductionInfoForm>({
        harvesterHeadId: new FormControl(null),
        productionBasePerformanceM3FubPerHour: new FormControl(null),
        fuelConsumptionInLitersPerG5h: new FormControl(null),
        oilConsumptionInLitersPerG5h: new FormControl(null),
        numberOfWheels: new FormControl(null),
        tireDimension: new FormControl(''),
        loadingCapacity: new FormControl(null),
        craneLength: new FormControl(null)
      })
    });

    this.setupFormSubscriptions();
  }

  private setupFormSubscriptions() {
    this.machineForm.get('baseInfo.machineManufacturerId')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(machineManufacturerId => {
      this.machineForm.get('baseInfo.machineModelText')?.setValue('');
      if (machineManufacturerId) {
        this.machineForm.get('baseInfo.machineModelText')?.enable();
        this.definitionService.loadMachineManufacturerModels(machineManufacturerId);
      } else {
        this.machineForm.get('baseInfo.machineModelText')?.disable();
      }
    });

    this.machineForm.get('baseInfo.ownerType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.machineForm.get('baseInfo.machineOwnerId')?.updateValueAndValidity();
      });

    this.machineForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkForChanges();
    });
  }

  private populateFormFromMachine() {
    if (!this.machineForm) {
      return;
    }

    this.definitionService.loadAllDefinitions();
    this.updateShowStoredOwnerName();

    const formValue = this.machineDetailsService.mapMachineToFormValue(this.machine);
    this.machineForm.patchValue(formValue, { emitEvent: false });

    const manufacturerId = this.machine.baseInfo.machineManufacturerId;
    if (manufacturerId) {
      this.machineForm.get('baseInfo.machineModelText')?.enable({ emitEvent: false });
      this.definitionService.loadMachineManufacturerModels(manufacturerId);
    }

    this.machineForm.get('baseInfo.machineOwnerId')?.updateValueAndValidity({ emitEvent: false });

    if (!this.authService.isPolicyPT()) {
      this.machineForm.disable();
    }

    if (this.isDemoMachine) {
      this.machineForm.get('baseInfo.ownerType')?.disable({ emitEvent: false });
      this.machineForm.get('biometriaInfo.biometriaCompanyId')?.disable({ emitEvent: false });
    }

    this.updateDemoMachineControlState();

    this.revalidateForm();
    this.machineForm.markAsPristine();
    this.machineFormHasChanges = false;
    this.refreshBaselineFromForm();
  }

  private checkForChanges(): void {
    if (!this.baselineUpdateModel) {
      return;
    }
    const { patch } = this.createPatchOperations();
    this.machineFormHasChanges = patch.length > 0;
  }

  private refreshBaselineFromForm(): void {
    if (!this.machineForm) {
      this.baselineUpdateModel = undefined;
      return;
    }

    const currentModel = this.machineDetailsService.createUpdateModel(this.machine, this.machineForm);
    this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
  }

  private cloneUpdateModel(model: UpdateMachineModel): UpdateMachineModel {
    return JSON.parse(JSON.stringify(model));
  }

  private createPatchOperations(currentUpdateMachineModel?: UpdateMachineModel): { patch: Operation[]; current: UpdateMachineModel } {
    const formValues = this.machineForm;

    const currentModel = currentUpdateMachineModel ?? this.machineDetailsService.createUpdateModel(this.machine, formValues);
    if (!this.baselineUpdateModel) {
      this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
    }

    return this.machineDetailsService.createPatchOperations(this.baselineUpdateModel, currentModel);
  }

  private onSuccessfulSave(currentModel: UpdateMachineModel): void {
    this.resetTeamStateAfterSave(currentModel);
    this.baselineUpdateModel = this.cloneUpdateModel(currentModel);
    this.machineHasOtherChanges = false;
    this.machineForm.markAsPristine();
    this.machineFormHasChanges = false;
  }

  private getTeams(): string {
    return this.machine.baseInfo.teams.filter(t =>
      t.clientStatus !== EditablePostClientStatus.Deleted)
      .map(team => `${team.teamId} ${team.name}`).join('\n')
  }

  navigateToTeam(team: MachineDetailsTeamInfoDto) {
    this.navigationService.navigateToTeamById(team);
  }

  navigateToContractor(event: MouseEvent, contractorId: number | null) {
    event.stopPropagation();
    this.navigationService.navigateToContractor(contractorId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isStanford2010VersionActive(version: Stanford2010Version): boolean {
    return version !== Stanford2010Version.FourPointOne || environment.activateStanford4p1;
  }

  async loadMachine(machineId?: number, forceReload: boolean = false) {
    if (this.isLoading && !forceReload) return;
    this.isLoading = true;
    const idParam = this.route.snapshot.paramMap?.get('machineId');
    let id = idParam != null ? parseInt(idParam) : 0;

    if (!id && this.machine?.id) {
      id = this.machine.id;
    }

    const targetId = machineId ?? id;
    if (!targetId) {
      this.isLoading = false;
      return;
    }

    this.machine = await firstValueFrom(this.machineApiService.getMachine(targetId));
    this.machine.thinningPerformance = this.machine.thinningPerformance || [];
    this.machine.regenerationPerformance = this.machine.regenerationPerformance || [];
    this.machine.machineWorkHours = this.machine.machineWorkHours || [];

    this.isLoading = false;
    this.machineHasOtherChanges = false;
    this.populateFormFromMachine();
  }



  saveChanges() {
    if (!this.validateFormForSave()) {
      return;
    }

    if (this.machineFormHasChanges || this.machineHasOtherChanges || this.machine.id === 0) {
      this.isLoading = true;
      this.saveMachineChanges().subscribe({
        next: () => this.isLoading = false,
        error: () => this.isLoading = false
      });
    }
  }


  private saveMachineChanges(): Observable<boolean> {
    const subject = new Subject<boolean>();

    if (!this.workHoursService.validate(this.machine.machineWorkHours)) {
      return of(false);
    }

    const currentModel = this.machineDetailsService.createUpdateModel(this.machine, this.machineForm);

    const machineIsNew = this.machine.id === 0;
    if (machineIsNew) {
      this.machineApiService.updateMachine(currentModel).subscribe({
        next: async (machineId: number) => {
          await this.loadNewMachineFromServer(machineId);
          subject.next(true);
          subject.complete();
        },
        error: () => {
          subject.next(false);
          subject.complete();
        }
      });
    } else {
      const { patch, current } = this.createPatchOperations(currentModel);
      const normalizedPatch = this.machineDetailsService.normalizePatchOperations(patch, current);

      if (normalizedPatch.length === 0) {
        this.machineDetailsService.applyUpdateModelToMachine(this.machine, current);
        this.onSuccessfulSave(current);
        this.refreshMachineAfterSave();
        return of(true);
      }

      const machineIdentifier = current.machineId ?? this.machine.baseInfo.machineId;
      this.machineApiService.patchMachine(this.machine.id, machineIdentifier, normalizedPatch).subscribe({
        next: async () => {
          this.machineDetailsService.applyUpdateModelToMachine(this.machine, current);
          this.onSuccessfulSave(current);
          await this.refreshMachineAfterSave();
          subject.next(true);
          subject.complete();
        },
        error: () => {
          subject.next(false);
          subject.complete();
        }
      });
    }

    return subject.asObservable();
  }

  private async loadNewMachineFromServer(machineId: number) {
    await this.loadMachine(machineId, true);
    this.machineIsNew = false;
    this.updateBrowserUrlAfterCreate(machineId);
    this.machineHasOtherChanges = false;
    this.machineForm.markAsPristine();
    this.machineFormHasChanges = false;
  }

  async duplicateMachine() {
    this.machineApiService.duplicateMachine(this.machine.id).subscribe((duplicateId: number) => {
      this.duplicatedFrom = this.machine.baseInfo.machineId ?? undefined;
      this.ngOnInit(duplicateId);
      this.router.navigate(['/machine/' + duplicateId]);
    });
  }

  onMachineChanged(changed: boolean) {
    this.machineHasOtherChanges = changed;
  }

  onMachineWorkHoursValidatesChanged(changed: boolean) {
    this.machineWorkHoursValidates = changed;
    this.revalidateForm();
  }

  createUpdateModel(form?: FormGroup<MachineDetailsForm>): UpdateMachineModel {
    return this.machineDetailsService.createUpdateModel(this.machine, form);
  }

  hasError(errorType: string): boolean | undefined {
    return this.companyIdField?.hasError(errorType);
  }

  getErrorMessage(controlName: string) {
    const control = this.machineForm.get(controlName);
    if (control?.errors) {
      const firstErrorKey = Object.keys(control.errors)[0];
      const errorValue = control.errors[firstErrorKey];
      return this.machineDetailsService.getPatternErrorMessage(firstErrorKey, controlName, errorValue);
    }
    return '';
  }

  async reloadMachineDetailsView() {
    await this.loadMachine();
    this.populateFormFromMachine();
  }

  addTeam() {
    const dialogRef = this.dialog.open(MachineAddTeamDialogComponent, {
      data: { machine: this.machine },
      maxWidth: '90vw',
    });
    dialogRef.afterClosed().subscribe(async (teams: MachineDetailsTeamInfoDto[]) => {
      if (!teams) {
        return;
      }

      this.machineHasOtherChanges = true;

      const formSnapshot = this.machineForm.getRawValue();

      teams.forEach((configuredTeam: MachineDetailsTeamInfoDto) => {
        const existingTeam = this.machine.baseInfo.teams.find(teamToDelete => teamToDelete.id === configuredTeam.id);

        if (configuredTeam.clientStatus === EditablePostClientStatus.Deleted) {
          if (existingTeam === undefined && !existingTeam) {
            return;
          }

          existingTeam.clientStatus = EditablePostClientStatus.Deleted;

          return;
        }

        if (existingTeam !== undefined && existingTeam) {
          existingTeam.clientStatus = undefined;
          return;
        }

        this.machine.baseInfo.teams.push(configuredTeam);
      })

      await this.handleDefaultMachinePerformances(teams);

      this.refreshTeamsInForm(formSnapshot);
      this.updateDemoMachineControlState();
    })
  }

  private updateDemoMachineControlState(): void {
    const demoMachineControl = this.machineForm.get('baseInfo.isDemoMachine');
    if (this.hasTeamConnections) {
      demoMachineControl?.disable({ emitEvent: false });
      return;
    }

    if (this.authService.isPolicyPT()) {
      demoMachineControl?.enable({ emitEvent: false });
    }
  }

  async handleDefaultMachinePerformances(teams: MachineDetailsTeamInfoDto[]) {
    const switchedProductTechnicianArea = this.determineSwitchedTechnicianArea(teams);
    if (switchedProductTechnicianArea === '') {
      return;
    }

    const areaEnum = GetProductionTechnicianAreaEnum(switchedProductTechnicianArea);
    await this.performanceService.applyArea(this.machine, areaEnum);
  }



  determineSwitchedTechnicianArea(teams: MachineDetailsTeamInfoDto[]): string {
    return this.machineDetailsService.determineSwitchedTechnicianArea(this.machine, teams);
  }

  canDeactivate(): Observable<boolean> {
    const isNewMachine = this.machine?.id === 0;
    const hasUnsavedChanges = this.machineHasOtherChanges || this.machineFormHasChanges || isNewMachine;

    if (!hasUnsavedChanges) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const dialogRef = this.dialog.open(SaveChangesDialogComponent, {
        data: {
          text: 'Det finns ändringar som inte är sparade. Vill du spara dem?',
          title: 'Spara ändringarna?',
          saveButton: 'Spara',
          discardButton: 'Spara inte'
        },
      });

      dialogRef.afterClosed().subscribe((result: SaveChangesResult) => {
        switch (result) {
          case SaveChangesResult.Save:
            this.saveMachineChanges().subscribe(success => {
              observer.next(success);
              observer.complete();
            });
            break;
          case SaveChangesResult.Discard:
            observer.next(true);
            observer.complete();
            break;
          case SaveChangesResult.Cancel:
          default:
            observer.next(false);
            observer.complete();
            break;
        }
      });
    });
  }

  goBack() {
    if (isPlatformBrowser(this.platformId)) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.router.navigate(['/machines']);
      }
    } else {
      this.router.navigate(['/machines']);
    }
  }

  private createNewMachine(dialogResult: CreateMachineModel): void {
    this.machine = this.machineDetailsService.createNewMachine(dialogResult);

    this.isReadonly = false;
    this.machineHasOtherChanges = true;
    this.populateFormFromMachine();
    this.machineOwnerService.onOwnerTypeChanged(this.machine.baseInfo.ownerType, this.machineForm, true);
    this.machineOwnerService.onOwnerChanged(this.machine.baseInfo.machineOwnerId, this.machineForm);
  }

  getDefaultMachinePerformanceAreaDisplayName(area: ProductionTechnicianArea) {
    return ProductionTechnicianAreaLabels[area]
  }

  conditionalContractorValidator(): ValidatorFn {
    return this.machineDetailsService.conditionalContractorValidator();
  }

  private async refreshMachineAfterSave(): Promise<void> {
    await this.loadMachine(this.machine.id, true);
  }

  private resetTeamStateAfterSave(currentModel: UpdateMachineModel): void {
    this.machineDetailsService.resetTeamStateAfterSave(this.machine, currentModel);
  }

  private revalidateForm(): void {
    if (!this.machineForm) {
      return;
    }

    this.machineForm.markAllAsTouched();
    this.machineForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  private validateFormForSave(): boolean {
    this.revalidateForm();

    if (!this.machineForm?.valid) {
      return false;
    }

    if (!this.machineWorkHoursValidates) {
      return false;
    }

    return true;
  }

  private refreshTeamsInForm(previousFormSnapshot?: ReturnType<typeof this.machineForm.getRawValue>): void {
    const snapshot = previousFormSnapshot ?? this.machineForm.getRawValue();
    const teamsValue = this.getTeams();

    this.machineForm.patchValue({
      baseInfo: {
        teams: teamsValue
      }
    }, { emitEvent: false });

    if (snapshot) {
      const controlsToRestore = [
        'registrationNumber',
        'manufacturingNumber',
        'ownerType',
        'machineOwnerId',
        'machineManufacturerId',
        'machineManufacturerModelId',
        'machineModelYear',
        'status',
        'otherEquipment',
        'isSchoolForwarder',
        'isGrotForwarder',
        'notes'
      ];

      controlsToRestore.forEach(controlName => {
        const value = snapshot.baseInfo?.[controlName as keyof typeof snapshot.baseInfo];
        if (value !== undefined) {
          this.machineForm.get(`baseInfo.${controlName}`)?.setValue(value as any, { emitEvent: false });
        }
      });
    }

    this.machineForm.markAsDirty();
    this.revalidateForm();
  }

  public async setDefaultPerformance(): Promise<void> {
    const changed = await this.performanceService.promptAndApplyDefault(this.machine);

    if (changed) {
      this.machineHasOtherChanges = true;
      this.machineForm?.markAsDirty();
    }
  }

  private updateBrowserUrlAfterCreate(machineId: number): void {
    const queryParams = { ...this.route.snapshot.queryParams } as Record<string, unknown>;
    delete queryParams['type'];

    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null) {
            searchParams.append(key, v.toString());
          }
        });
      } else {
        searchParams.set(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const newUrl = queryString ? `/machine/${machineId}?${queryString}` : `/machine/${machineId}`;
    this.location.replaceState(newUrl);
  }

  onOwnerTypeChanged(ownerType: MachineOwnerType) {
    this.machineOwnerService.onOwnerTypeChanged(ownerType, this.machineForm);
  }

  onOwnerChanged(machineOwnerId: number) {
    this.machineOwnerService.onOwnerChanged(machineOwnerId, this.machineForm);
  }
}